import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { api } from './api.js';
import Login from './routes/Login.jsx';
import Projects from './routes/Projects.jsx';
import Editor from './routes/Editor.jsx';
import Share from './routes/Share.jsx';
import SystemAdmin from './routes/SystemAdmin.jsx';
import PasswordChangeDialog from './components/PasswordChangeDialog.jsx';
import { Toasts, ToastProvider } from './components/Toast.jsx';
import ActivityStatus from './components/ActivityStatus.jsx';
import { Monogram } from './components/Brand.jsx';

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

function Loading({ what = 'Loading' }) {
  return <div className="loading app-loading"><Monogram className="loading-monogram" />{what}…</div>;
}

function RequireAuth({ children }) {
  const { user, ready, connectionError, refresh } = useAuth();
  const loc = useLocation();
  if (!ready) return <Loading />;
  if (connectionError && !user) return <div className="centred"><div className="card"><h1>Connection unavailable</h1><p role="alert">{connectionError}</p><button className="btn primary" onClick={refresh}>Try again</button></div></div>;
  if (!user) return <Navigate to="/login" replace state={{ from: loc.pathname + loc.search }} />;
  return children;
}
function TenantOnly({ children }) { const { user } = useAuth(); return user?.isSystemAdmin ? <Navigate to="/system" replace /> : children; }
function SystemOnly({ children }) { const { user } = useAuth(); return user?.isSystemAdmin ? children : <Navigate to="/" replace />; }

export default function App() {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);
  const [connectionError, setConnectionError] = useState('');

  const refresh = useCallback(async () => {
    try {
      const r = await api.auth.me();
      setUser(r.user);
      setConnectionError('');
    } catch (e) {
      if (e.status === 401) { setUser(null); setConnectionError(''); }
      else setConnectionError(e.message);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const logout = useCallback(async () => {
    try { await api.auth.logout(); } finally { setUser(null); }
  }, []);

  return (
    <AuthCtx.Provider value={{ user, ready, setUser, refresh, logout, connectionError }}>
      <ToastProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/s/:token" element={<Share />} />
          <Route path="/" element={<RequireAuth><TenantOnly><Projects /></TenantOnly></RequireAuth>} />
          <Route path="/system" element={<RequireAuth><SystemOnly><SystemAdmin /></SystemOnly></RequireAuth>} />
          <Route path="/p/:id" element={<RequireAuth><TenantOnly><Editor /></TenantOnly></RequireAuth>} />
          <Route path="/samples/:id" element={<RequireAuth><TenantOnly><Editor sampleMode /></TenantOnly></RequireAuth>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toasts />
        <ActivityStatus />
        <PasswordChangeDialog />
      </ToastProvider>
    </AuthCtx.Provider>
  );
}

export { Loading };
